'use client';

import { AppCard } from '@ui/components/layouts';
import { text } from '@modules/dashboard/text';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faCalendar } from '@micks-yard/icons/solid/faCalendar';
import { faClock } from '@micks-yard/icons/solid/faClock';
import { faCloud } from '@micks-yard/icons/solid/faCloud';
import { faCloudSun } from '@micks-yard/icons/solid/faCloudSun';
import { faDroplet } from '@micks-yard/icons/solid/faDroplet';
import { faLeaf } from '@micks-yard/icons/solid/faLeaf';
import { faSpinner } from '@micks-yard/icons/solid/faSpinner';
import { faSun } from '@micks-yard/icons/solid/faSun';
import { faWind } from '@micks-yard/icons/solid/faWind';

import type { DateWeatherWidgetProps } from '@modules/dashboard/containers/date_weather_widget_container';

function getWeatherIcon(iconName: string) {
  switch (iconName) {
    case 'sun':
      return <FaIcon icon={faSun} className="size-3.5 text-yellow-500" />;
    case 'cloud-sun':
      return <FaIcon icon={faCloudSun} className="size-3.5 text-gray-500" />;
    case 'cloud':
      return <FaIcon icon={faCloud} className="size-3.5 text-gray-400" />;
    default:
      return <FaIcon icon={faSun} className="size-3.5 text-yellow-500" />;
  }
}

function seasonColorToClassName(seasonColor: string) {
  switch (seasonColor) {
    case 'text-green-600':
      return 'text-green-600';
    case 'text-yellow-600':
      return 'text-yellow-600';
    case 'text-orange-600':
      return 'text-orange-600';
    case 'text-blue-600':
      return 'text-blue-600';
    default:
      return 'text-green-600';
  }
}

interface QuickChipProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function QuickChip({ icon, label, value }: QuickChipProps) {
  return (
    <div className="border-border bg-muted flex items-center gap-1.5 rounded-full border px-2.5 py-1">
      <span className="shrink-0">{icon}</span>
      <span className="text-muted-foreground text-xs">{label}:</span>
      <span className="text-foreground text-xs font-medium">{value}</span>
    </div>
  );
}

export function DateWeatherWidgetView({ data, isLoadingWeather }: DateWeatherWidgetProps) {
  const formattedDate = `${data.dayOfWeek}, ${data.day} ${data.month} ${data.year}`;

  return (
    <AppCard className="px-3 py-2">
      <div className="flex flex-wrap items-start gap-2 md:gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
            {text.timeLabel}
          </span>
          <QuickChip
            icon={<FaIcon icon={faClock} className="size-3.5 text-blue-500" />}
            label={text.now}
            value={data.time}
          />
          <QuickChip
            icon={<FaIcon icon={faCalendar} className="size-3.5 text-blue-500" />}
            label={text.date}
            value={formattedDate}
          />
          <QuickChip
            icon={
              <span className={seasonColorToClassName(data.seasonColor)}>
                <FaIcon icon={faLeaf} className="size-3.5" />
              </span>
            }
            label={text.season}
            value={data.season}
          />
        </div>

        <div className="bg-border hidden w-px self-stretch md:flex" />

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
            {text.weather}
          </span>

          {isLoadingWeather ? (
            <div className="border-border bg-muted flex items-center gap-1.5 rounded-full border px-2.5 py-1">
              <FaIcon icon={faSpinner} className="text-foreground size-3.5 animate-spin" />
              <span className="text-foreground text-xs">{text.weatherLoading}</span>
            </div>
          ) : data.weather ? (
            <>
              <QuickChip
                icon={getWeatherIcon(data.weather.icon)}
                label={text.now}
                value={`${data.weather.condition} ${data.weather.temperature}°C`}
              />
              <QuickChip
                icon={<FaIcon icon={faWind} className="size-3.5 text-gray-500" />}
                label={text.wind}
                value={`${data.weather.windSpeed} km/h`}
              />
              <QuickChip
                icon={<FaIcon icon={faDroplet} className="size-3.5 text-gray-500" />}
                label={text.humidity}
                value={`${data.weather.humidity}%`}
              />
            </>
          ) : (
            <div className="border-border bg-muted flex items-center gap-1.5 rounded-full border px-2.5 py-1">
              <FaIcon icon={faCloudSun} className="size-3.5 text-gray-500" />
              <span className="text-foreground text-xs">{text.weatherUnavailable}</span>
            </div>
          )}
        </div>
      </div>
    </AppCard>
  );
}
